/// <reference types="vite/client" />

// The operator's API secret key, remembered in localStorage so a reload or a browser restart does
// not ask for it again. The expiry slides: every successful authenticated call pushes it 30 days
// out, so a key in regular use never lapses, while one left untouched for 30 days is forgotten.
// [[spec-in-repo-react-frontend-for-arb-bot-with-an-openapi-and-orval-contract]] R6

export const SESSION_STORAGE_KEY = "arb-bot.session";
export const SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

type SignOutReason = "logout" | "rejected";

interface StoredSession {
    key: string;
    expiresAt: number;
}

export interface SessionSnapshot {
    key: string | null;
    /** Why the last session ended, when the operator did not end it themselves. */
    notice: string | null;
}

const NOTICES: Record<SignOutReason, string | null> = {
    logout: null,
    rejected: "The bot rejected the key (401). Log in again.",
};

const listeners = new Set<() => void>();
let snapshot: SessionSnapshot = { key: readStored(), notice: null };

function readStored(): string | null {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (raw === null) return null;
    const stored = parse(raw);
    if (stored === null || stored.expiresAt <= Date.now()) {
        localStorage.removeItem(SESSION_STORAGE_KEY);
        return null;
    }
    return stored.key;
}

function parse(raw: string): StoredSession | null {
    try {
        const value: unknown = JSON.parse(raw);
        if (typeof value !== "object" || value === null) return null;
        const { key, expiresAt } = value as Partial<StoredSession>;
        return typeof key === "string" && key.length > 0 && typeof expiresAt === "number" ? { key, expiresAt } : null;
    } catch {
        return null;
    }
}

function persist(key: string): void {
    const stored: StoredSession = { key, expiresAt: Date.now() + SESSION_TTL_MS };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stored));
}

function publish(next: SessionSnapshot): void {
    snapshot = next;
    listeners.forEach((listener) => listener());
}

/**
 * `false` when the bot serves its API without a key: the throwaway `verify:isolated` stack sets
 * `VITE_API_AUTH=off`, so the app opens without a login. Read per call, so tests can stub it.
 * [[spec-hot-reloading-auth-free-self-cleaning-verify-stack-with-realistic-multi-dex-fixtures]] R2
 */
export function authRequired(): boolean {
    return import.meta.env.VITE_API_AUTH !== "off";
}

export function getSessionSnapshot(): SessionSnapshot {
    return snapshot;
}

export function subscribeSession(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** The key to send, or `null` once it has expired, even mid-session. */
export function currentKey(): string | null {
    if (snapshot.key !== null && readStored() === null) publish({ key: null, notice: null });
    return snapshot.key;
}

export function signIn(key: string): void {
    persist(key);
    publish({ key, notice: null });
}

/** Slides the expiry after a successful authenticated call. */
export function touchSession(): void {
    if (snapshot.key !== null && readStored() === snapshot.key) persist(snapshot.key);
}

export function signOut(reason: SignOutReason): void {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    publish({ key: null, notice: NOTICES[reason] });
}

/** Re-reads storage, as a fresh page load would. Tests only. */
export function reloadSessionForTests(): void {
    snapshot = { key: readStored(), notice: null };
}
